import {FC, memo, useCallback} from 'react';
import {FileRejection, useDropzone} from 'react-dropzone';
import {Box, Center, Span, Stack} from '@chakra-ui/react';
import {
  extractFileExtension,
  useT,
  ALLOWED_DOC_FILES,
  TAllowedDocFiles,
  IDocumentQueueItem
} from '@kcast-fe/core';
import {UPLOAD_ACCEPT, UPLOAD_MAX_MB, UPLOAD_MAX_BYTES} from 'core';
import {Tooltip, useScreenSize} from 'ui-kit';
import {v4 as uuidv4} from 'uuid';

interface IProps {
  disabled?: boolean;
  onSelectFile: (file: File) => void;
  onFileError: (item: IDocumentQueueItem) => void;
}

const TOOLTIP_ID = 'DROPZONE_ACCEPT_FORMATS';

const DocDropzoneFC: FC<IProps> = ({disabled, onFileError, onSelectFile}) => {
  const {isLessThan768} = useScreenSize();
  const {t} = useT();

  const handleFileError = useCallback(
    (file: File | null, error: string, isType: boolean) => {
      onFileError({
        id: uuidv4(),
        error: error,
        title: file?.name ?? null,
        type: isType && file ? (extractFileExtension(file.name) as TAllowedDocFiles) : null,
        createdAt: new Date().toISOString(),
        progress: 0,
        isDone: false,
        isError: true
      });
    },
    [onFileError]
  );

  const onDrop = useCallback(
    (files: Array<File>, fileRejections: FileRejection[]) => {
      fileRejections.forEach((i) => {
        handleFileError(i.file, t('errors.docIsUnsupported', {name: i.file.name}), false);
      });

      files.forEach((file) => {
        if (file.size > UPLOAD_MAX_BYTES) {
          handleFileError(file, t('errors.docIsLarge', {name: file.name}), true);
        } else {
          onSelectFile(file);
        }
      });
    },
    [handleFileError, onSelectFile, t]
  );

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    accept: UPLOAD_ACCEPT,
    disabled: disabled,
    multiple: true,
    onDrop
  });

  return (
    <Stack gap={0} opacity={disabled ? 0.6 : 1}>
      <Center
        h={'80px'}
        {...getRootProps()}
        borderRadius={'12px'}
        bg={'{colors.btnPrimarySuperlight}'}
        transition={'border var(--tr-100-ei)'}
        border={isDragActive ? '4px solid' : 'thin dashed'}
        borderColor={'{colors.textIconsLink}'}
        _hover={{cursor: 'pointer'}}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <Box>{t('dropzone.dropHere')}</Box>
        ) : (
          <Box textAlign={'center'}>
            <Box>
              {!isLessThan768 && <>{t('dropzone.dragDropOr')}</>}
              <Span color={'{colors.textLink}'}>{t('dropzone.chooseFile')}</Span>
              {` ${t('dropzone.toUpload')}`}
            </Box>
            <Box fontSize={'13px'} color={'{colors.textPrimarySuperLight}'}>
              {t('dropzone.size', {size: UPLOAD_MAX_MB})}
              {!isLessThan768 && (
                <>
                  {'. '}
                  <Span data-tooltip-id={TOOLTIP_ID} textDecoration={'underline'}>
                    {t('dropzone.formats')}
                  </Span>

                  <Tooltip id={TOOLTIP_ID}>
                    <Box textTransform={'uppercase'}>{ALLOWED_DOC_FILES.join(', ')}</Box>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
        )}
      </Center>
    </Stack>
  );
};

export const DocDropzone = memo(DocDropzoneFC);
